import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';

const ROLES = [
  { value: 'admin', label: 'Admin',          badge: 'badge-yellow' },
  { value: 'mloc',  label: 'Multi-Location', badge: 'badge-purple' },
  { value: 'mgr',   label: 'Club Manager',   badge: 'badge-blue' },
  { value: 'fd',    label: 'Front Desk',     badge: 'badge-gray' },
];

const ALL_CLUBS = ['WA','MF','GB','DEN','LEW','MCK'];
const CLUB_NAMES = {
  WA:'West Allis', MF:'Menomonee Falls', GB:'Green Bay',
  DEN:'Denton', LEW:'Lewisville', MCK:'McKinney',
};

// ── Manager PIN modal (Sprint 19.10) ─────────────────────────────────────────
// Sets/changes a single club's PIN. Two entries required so a typo doesn't
// silently lock managers out. The PIN itself is only ever sent once, over
// HTTPS, to be hashed server-side (scrypt) — never stored or displayed
// anywhere after this, including here.
function ManagerPinModal({ clubId, onClose, onSaved }) {
  const { put } = useApi();
  const [pin, setPin]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  async function handleSave() {
    if (pin.length < 4) { setError('PIN must be at least 4 characters.'); return; }
    if (pin !== confirm) { setError('PINs don\'t match.'); return; }
    setSaving(true); setError(null);
    try {
      await put(`/users/manager-pins/${clubId}`, { pin });
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:380, width:'100%' }}>
        <div className="modal-title">Set Manager PIN — {CLUB_NAMES[clubId]}</div>

        {error && (
          <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', border:'1px solid var(--red)', borderRadius:6, fontSize:13, color:'var(--red)', marginBottom:16 }}>
            {error}
          </div>
        )}

        <div className="field-group">
          <label className="field-label">New PIN</label>
          <input className="field-input" type="password" maxLength={6} value={pin}
            onChange={e=>setPin(e.target.value)} placeholder="4-6 digits" autoFocus />
        </div>
        <div className="field-group">
          <label className="field-label">Confirm PIN</label>
          <input className="field-input" type="password" maxLength={6} value={confirm}
            onChange={e=>setConfirm(e.target.value)} placeholder="Re-enter PIN" />
        </div>

        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:16 }}>
          One PIN per club, shared by all managers there — not tied to an individual login.
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save PIN'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Manager PINs panel (Sprint 19.10) ────────────────────────────────────────
// Enforcement mechanism behind "retention is manager-owned" — a valid PIN is
// required to log a cancellation contact in the Retention module. Never
// fetches or displays the actual PIN value or hash — only whether one is set.
function ManagerPinsPanel() {
  const { get } = useApi();
  const [pins, setPins]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [editClub, setEditClub] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    get('/users/manager-pins')
      .then(d => setPins(d.pins || {}))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ marginBottom:24, background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:8, padding:'14px 16px' }}>
      {editClub && (
        <ManagerPinModal
          clubId={editClub}
          onClose={() => setEditClub(null)}
          onSaved={() => { setEditClub(null); load(); }}
        />
      )}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ fontSize:14, fontWeight:700 }}>🔐 Manager PINs</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>Required to log a cancellation contact in Retention</div>
      </div>
      {loading ? (
        <div style={{ padding:12, textAlign:'center' }}><div className="spinner" /></div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8 }}>
          {ALL_CLUBS.map(id => {
            const entry = pins[id];
            return (
              <div key={id} style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                background:'var(--surface-3)', border:'1px solid var(--border)',
                borderRadius:6, padding:'8px 10px',
              }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{CLUB_NAMES[id]}</div>
                  <div style={{ fontSize:10, color: entry?.isSet ? '#22c55e' : 'var(--text-muted)' }}>
                    {entry?.isSet ? '✓ PIN set' : 'Not set'}
                  </div>
                </div>
                <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px' }}
                  onClick={() => setEditClub(id)}>
                  {entry?.isSet ? 'Change' : 'Set'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getInitials(name, email) {
  const n = name || email || '??';
  return n.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}
function avatarColor(str) {
  const colors = ['#7c3aed','#2563eb','#059669','#d97706','#dc2626','#0891b2','#db2777'];
  let h = 0;
  for (let i = 0; i < (str||'').length; i++) h = (h*31+str.charCodeAt(i))&0xffff;
  return colors[h % colors.length];
}

function Avatar({ name, email, size=38 }) {
  const str = name || email || '??';
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:avatarColor(str),
      display:'flex', alignItems:'center', justifyContent:'center',
      fontWeight:700, fontSize:size*0.36, color:'#fff', flexShrink:0 }}>
      {getInitials(name, email)}
    </div>
  );
}

function RoleBadge({ role }) {
  const r = ROLES.find(x => x.value === role);
  if (!r) return <span className="badge badge-gray">{role}</span>;
  return <span className={`badge ${r.badge}`}>{r.label}</span>;
}

function ClubChips({ clubs }) {
  if (!clubs || clubs.length === 0) return <span style={{ color:'var(--text-muted)', fontSize:12 }}>—</span>;
  if (ALL_CLUBS.every(c => clubs.includes(c))) return (
    <span style={{ background:'rgba(234,179,8,0.15)', color:'var(--yellow)', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:4 }}>All Clubs</span>
  );
  return (
    <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
      {clubs.map(c => (
        <span key={c} style={{ background:'var(--surface-3)', color:'var(--text-muted)', fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:3 }}>
          {c}
        </span>
      ))}
    </div>
  );
}

// ── User Modal (Invite + Edit) ────────────────────────────────────────────────
function UserModal({ user, onClose, onSaved }) {
  const { post, put } = useApi();
  const isEdit = !!user;
  const [form, setForm] = useState({
    email:  user?.email  || '',
    name:   user?.name   || '',
    role:   user?.role   || 'fd',
    clubs:  user?.clubs  || [],
    status: user?.status || 'invited',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const set = (f, v) => setForm(p => ({...p, [f]: v}));
  const toggleClub = id => setForm(p => ({
    ...p, clubs: p.clubs.includes(id) ? p.clubs.filter(c=>c!==id) : [...p.clubs, id]
  }));

  async function handleSave() {
    if (!form.email.trim()) { setError('Email is required.'); return; }
    if (form.role !== 'admin' && form.clubs.length === 0) { setError('Select at least one club.'); return; }
    setSaving(true); setError(null);
    try {
      const clubArr = form.role === 'admin' ? ALL_CLUBS : form.clubs;
      if (isEdit) {
        await put(`/users/${user.id}/clubs`, { clubs: clubArr, role: form.role, name: form.name || null, status: form.status });
      } else {
        const result = await post('/users/invite', { email: form.email.trim().toLowerCase(), name: form.name.trim() || null, role: form.role, clubs: clubArr });
        if (result && !result.magicLinkSent) {
          console.warn('User created in COMS but magic link may not have sent — check Auth0');
        }
      }
      onSaved();
    } catch (err) { setError(err.message || 'Failed to save.'); setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:480, width:'100%' }}>
        <div className="modal-title">{isEdit ? `Edit — ${user.name || user.email}` : '+ Invite User'}</div>

        {error && (
          <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', border:'1px solid var(--red)', borderRadius:6, fontSize:13, color:'var(--red)', marginBottom:16 }}>
            {error}
          </div>
        )}

        <div className="field-group">
          <label className="field-label">Email *</label>
          <input className="field-input" type="email" value={form.email} disabled={isEdit}
            onChange={e=>set('email',e.target.value)} placeholder="staff@example.com" autoFocus={!isEdit} />
        </div>

        <div className="field-group">
          <label className="field-label">Name</label>
          <input className="field-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Full name (optional)" />
        </div>

        <div className="field-group">
          <label className="field-label">Role</label>
          <select className="field-select" value={form.role} onChange={e=>set('role',e.target.value)}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        {isEdit && (
          <div className="field-group">
            <label className="field-label">Status</label>
            <select className="field-select" value={form.status} onChange={e=>set('status',e.target.value)}>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}

        {form.role !== 'admin' ? (
          <div className="field-group">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <label className="field-label" style={{ margin:0 }}>Club Access</label>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={()=>setForm(p=>({...p,clubs:[...ALL_CLUBS]}))} style={{ fontSize:11, color:'var(--yellow)', background:'none', border:'none', cursor:'pointer', padding:0 }}>Select All</button>
                <button onClick={()=>setForm(p=>({...p,clubs:[]}))} style={{ fontSize:11, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', padding:0 }}>Clear</button>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              {ALL_CLUBS.map(id => (
                <label key={id} style={{
                  display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13,
                  padding:'7px 10px',
                  background: form.clubs.includes(id) ? 'rgba(234,179,8,0.1)' : 'var(--surface-2)',
                  border: `1px solid ${form.clubs.includes(id) ? 'var(--yellow)' : 'var(--border)'}`,
                  borderRadius:6
                }}>
                  <input type="checkbox" checked={form.clubs.includes(id)} onChange={()=>toggleClub(id)} />
                  <span>{CLUB_NAMES[id]}</span>
                </label>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ padding:'10px 14px', background:'rgba(234,179,8,0.08)', border:'1px solid rgba(234,179,8,0.2)', borderRadius:6, fontSize:13, color:'var(--yellow)', marginBottom:16 }}>
            ✓ Admin users have access to all 6 clubs automatically.
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminUsers() {
  const { get, post } = useApi();
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [clubFilter, setClubFilter] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [editUser,   setEditUser]   = useState(null);

  const [resending, setResending] = useState(null);

  async function resendInvite(u) {
    setResending(u.id);
    try {
      const clubArr = u.role === 'admin' ? ALL_CLUBS : (u.clubs || []);
      await post('/users/invite', {
        email: u.email,
        name:  u.name || null,
        role:  u.role,
        clubs: clubArr,
      });
      alert(`Invite resent to ${u.email}`);
    } catch (err) {
      alert(`Failed to resend: ${err.message}`);
    } finally {
      setResending(null);
    }
  }

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (search)     p.set('search', search);
    if (roleFilter) p.set('role',   roleFilter);
    if (clubFilter) p.set('club',   clubFilter);
    get(`/users${p.toString() ? '?'+p.toString() : ''}`)
      .then(d => setUsers(d.users || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, roleFilter, clubFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function statusStyle(s) {
    if (s === 'active')   return { bg:'rgba(34,197,94,0.15)',   color:'#22c55e' };
    if (s === 'inactive') return { bg:'rgba(239,68,68,0.15)',   color:'#ef4444' };
    return                       { bg:'rgba(234,179,8,0.15)',   color:'var(--yellow)' };
  }

  const counts = {
    total:    users.length,
    active:   users.filter(u=>u.status==='active').length,
    invited:  users.filter(u=>u.status==='invited').length,
    inactive: users.filter(u=>u.status==='inactive').length,
  };

  return (
    <div>
      {(showInvite || editUser) && (
        <UserModal
          user={editUser}
          onClose={() => { setShowInvite(false); setEditUser(null); }}
          onSaved={() => { setShowInvite(false); setEditUser(null); load(); }}
        />
      )}

      <ManagerPinsPanel />

      {/* KPI bar */}
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        {[
          { label:'Total',    value:counts.total,    color:'var(--text)' },
          { label:'Active',   value:counts.active,   color:'#22c55e' },
          { label:'Invited',  value:counts.invited,  color:'var(--yellow)' },
          { label:'Inactive', value:counts.inactive, color:'#ef4444' },
        ].map(k => (
          <div key={k.label} style={{ background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 16px', textAlign:'center', minWidth:80 }}>
            <div style={{ fontSize:22, fontWeight:700, color:k.color, fontFamily:'DM Mono, monospace' }}>{k.value}</div>
            <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1 }}>{k.label}</div>
          </div>
        ))}
        <div style={{ flex:1 }} />
        <button className="btn btn-primary" onClick={() => { setEditUser(null); setShowInvite(true); }}>+ Invite User</button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <input className="field-input" placeholder="Search by name or email…"
          value={search} onChange={e=>setSearch(e.target.value)}
          style={{ flex:1, minWidth:200, maxWidth:320 }} />
        <select className="field-select" value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={{ maxWidth:180 }}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select className="field-select" value={clubFilter} onChange={e=>setClubFilter(e.target.value)} style={{ maxWidth:180 }}>
          <option value="">All Clubs</option>
          {ALL_CLUBS.map(id => <option key={id} value={id}>{CLUB_NAMES[id]}</option>)}
        </select>
        {(search||roleFilter||clubFilter) && (
          <button onClick={()=>{setSearch('');setRoleFilter('');setClubFilter('');}}
            style={{ background:'none', border:'1px solid var(--border)', color:'var(--text-muted)', borderRadius:5, padding:'4px 12px', fontSize:12, cursor:'pointer' }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* User cards */}
      {loading ? (
        <div style={{ padding:40, textAlign:'center' }}><div className="spinner" /></div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <div className="empty-state-text">
            {search||roleFilter||clubFilter ? 'No users match your filters.' : 'No users yet — invite your first staff member.'}
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {users.map(u => {
            const sc = statusStyle(u.status);
            return (
              <div key={u.id} style={{
                background:'var(--surface-2)', border:'1px solid var(--border)',
                borderRadius:8, padding:'12px 16px',
                display:'flex', alignItems:'center', gap:14,
              }}>
                <Avatar name={u.name} email={u.email} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
                    <span style={{ fontWeight:600, fontSize:14 }}>{u.name || '—'}</span>
                    <RoleBadge role={u.role} />
                    <span style={{ background:sc.bg, color:sc.color, fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:4 }}>
                      {(u.status||'invited').toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>{u.email}</div>
                  <ClubChips clubs={u.clubs} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6, flexShrink:0 }}>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                    {u.lastLogin
                      ? `Last login ${new Date(u.lastLogin).toLocaleDateString()}`
                      : u.status === 'invited' ? '⏳ Invite pending' : 'Never logged in'}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                    Invited {u.invitedAt ? new Date(u.invitedAt).toLocaleDateString() : '—'}
                  </div>
                  <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 12px' }}
                    onClick={() => setEditUser(u)}>
                    ✎ Edit
                  </button>
                  {u.status === 'invited' && !u.lastLogin && (
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize:11, padding:'3px 12px', color:'var(--yellow)', borderColor:'rgba(234,179,8,0.4)' }}
                      disabled={resending === u.id}
                      onClick={() => resendInvite(u)}>
                      {resending === u.id ? 'Sending…' : '↺ Resend Invite'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
